import React, { useState, useEffect } from 'react';
import { DollarSign, ArrowLeft } from 'lucide-react';
import { TagBudgetChart } from '../components/charts/TagBudgetChart';
import { YearlyBudgetTable } from '../components/charts/YearlyBudgetTable';
import { CustomerTimeAllocationChart } from '../components/charts/CustomerTimeAllocationChart';
import { ProjectTypeAllocationChart } from '../components/charts/ProjectTypeAllocationChart';
import { ProjectTypePieChart } from '../components/charts/ProjectTypePieChart';
import { CustomerProfitabilityChart } from '../components/charts/CustomerProfitabilityChart';
import { ForecastChart } from '../components/charts/ForecastChart';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import {
  initializeBudgetMap,
  initializeTimeMap,
  calculateCustomerDurations,
  processTimeAllocation,
  formatTimeAllocationData,
  calculateProjectTypeAllocation,
  calculateProfitability,
  processBudgetData,
  processProjections,
  formatProcessedBudgets,
  formatBudget
} from '../utils/finance';

const CHART_COLORS = [
  '#3B82F6', // blue-500
  '#EF4444', // red-500
  '#10B981', // emerald-500
  '#F59E0B', // amber-500
  '#8B5CF6', // violet-500
  '#EC4899', // pink-500
  '#06B6D4', // cyan-500
  '#F97316', // orange-500
  '#6366F1', // indigo-500
];

export function FinanceAnalysis() {
  const [selectedYear, setSelectedYear] = useState('2024');
  const [years] = useState(['2018', '2019', '2020', '2021', '2022', '2023', '2024', '2025']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [customerBudgets, setCustomerBudgets] = useState<any[]>([]);
  const [timeAllocation, setTimeAllocation] = useState<any>({
    data: [],
    customers: [],
    colors: {}
  });
  const [projectTypeAllocation, setProjectTypeAllocation] = useState<any[]>([]);
  const [profitabilityData, setProfitabilityData] = useState<any[]>([]);
  const [forecastData, setForecastData] = useState<any[]>([]);
  const [timeframe, setTimeframe] = useState({
    start: '',
    end: '',
    ticks: [] as string[]
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [projectsResponse, customersResponse, projectionsResponse] = await Promise.all([
        supabase
          .from('projects')
          .select(`
            id,
            project_name,
            budget,
            year,
            month,
            duration:durations (
              name
            ),
            customer:customers (
              id,
              name,
              color
            ),
            tags:project_tags (
              tag:tags (
                id,
                name
              )
            )
          `),
        supabase
          .from('customers')
          .select('*')
          .order('name'),
        supabase
          .from('projections')
          .select('*')
          .eq('status', 'alive')
      ]);

      if (projectsResponse.error) throw projectsResponse.error;
      if (customersResponse.error) throw customersResponse.error;
      if (projectionsResponse.error) throw projectionsResponse.error;

      // Initialize maps
      const budgetMap = initializeBudgetMap(customersResponse.data, years);
      const timeMap = initializeTimeMap(customersResponse.data, years);

      // Calculate customer durations
      const customerTotalDuration = calculateCustomerDurations(projectsResponse.data);
      const activeCustomers = Array.from(customerTotalDuration.entries())
        .filter(([_, duration]) => duration > 0)
        .map(([name]) => name);

      // Process time allocation
      processTimeAllocation(projectsResponse.data, timeMap, years);

      // Process budget data
      const forecastMap = processBudgetData(projectsResponse.data, budgetMap, years);

      // Process projections
      processProjections(projectionsResponse.data, forecastMap, years);

      // Format processed data
      const processedBudgets = formatProcessedBudgets(budgetMap);
      const formattedTimeAllocation = formatTimeAllocationData(timeMap, years, activeCustomers, CHART_COLORS);
      const projectTypes = calculateProjectTypeAllocation(projectsResponse.data, years);
      const profitability = calculateProfitability(timeMap, budgetMap, activeCustomers);

      // Format forecast data
      const formattedForecastData = years.map(year => ({
        year,
        ...forecastMap.get(year)
      }));

      setTimeframe({
        start: years[0],
        end: years[years.length - 1],
        ticks: years
      });

      setCustomerBudgets(processedBudgets);
      setTimeAllocation(formattedTimeAllocation);
      setProjectTypeAllocation(projectTypes);
      setProfitabilityData(profitability);
      setForecastData(formattedForecastData);

    } catch (err) {
      console.error('Error loading finance data:', err);
      setError(err instanceof Error ? err.message : 'An error occurred while loading data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-600">Loading financial data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-red-600">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <DollarSign className="w-8 h-8 text-indigo-600 mr-3" />
            <h1 className="text-2xl font-bold text-gray-900">Financial Analysis</h1>
          </div>
          <Link
            to="/finance"
            className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Finance
          </Link>
        </div>

        {/* First row: Budget by Tag and Time Allocation by Customer */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <TagBudgetChart formatBudget={formatBudget} />
          <CustomerTimeAllocationChart
            data={timeAllocation.data}
            customers={timeAllocation.customers}
            colors={timeAllocation.colors}
          />
        </div>

        {/* Second row: Project Type Allocation and Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <ProjectTypeAllocationChart data={projectTypeAllocation} />
          <ProjectTypePieChart
            data={projectTypeAllocation}
            selectedYear={selectedYear}
            onYearChange={setSelectedYear}
          />
        </div>

        {/* Customer Profitability Chart */}
        <div className="mb-6">
          <CustomerProfitabilityChart
            data={profitabilityData}
            formatBudget={formatBudget}
          />
        </div>

        {/* Yearly Budget Table */}
        <div className="mb-6">
          <YearlyBudgetTable
            customerBudgets={customerBudgets}
            years={years}
            formatBudget={formatBudget}
          />
        </div>

        {/* Revenue Forecast */}
        <ForecastChart
          data={forecastData}
          timeframe={timeframe}
          formatBudget={formatBudget}
        />
      </div>
    </div>
  );
}